import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { adminAPI, brandingAPI } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon,
  PaintBrushIcon,
  CreditCardIcon,
  RocketLaunchIcon,
  CheckCircleIcon,
  XCircleIcon,
  LinkIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
} from '@heroicons/react/24/outline';

const STEPS = [
  { id: 'company_info', label: 'Company Info', icon: BuildingOfficeIcon },
  { id: 'branding', label: 'Branding', icon: PaintBrushIcon },
  { id: 'stripe', label: 'Connect Stripe', icon: CreditCardIcon },
  { id: 'verify', label: 'Verify & Go Live', icon: RocketLaunchIcon },
];

function AdminOnboarding() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState(0);

  // Company info form state
  const [companyInfo, setCompanyInfo] = useState({
    companyName: '',
    companyAddress: '',
    supportEmail: '',
    taxId: '',
    invoicePrefix: 'INV',
    timezone: 'America/New_York',
  });

  // Branding form state
  const [branding, setBranding] = useState({
    logo_url: '',
    app_name: '',
    primary_color: '#3B82F6',
    secondary_color: '#10B981',
    favicon_url: '',
  });

  // Load general settings
  const { isLoading: settingsLoading } = useQuery(
    'onboarding-general-settings',
    () => adminAPI.getGeneralSettings(),
    {
      select: (response) => response.data,
      onSuccess: (data) => {
        setCompanyInfo({
          companyName: data.companyName || '',
          companyAddress: data.companyAddress || '',
          supportEmail: data.supportEmail || '',
          taxId: data.taxId || '',
          invoicePrefix: data.invoicePrefix || 'INV',
          timezone: data.timezone || 'America/New_York',
        });
      },
    }
  );

  // Load branding
  const { isLoading: brandingLoading } = useQuery(
    'onboarding-branding',
    () => brandingAPI.getBranding(),
    {
      select: (response) => response.data,
      onSuccess: (data) => {
        setBranding({
          logo_url: data.logo_url || '',
          app_name: data.app_name || '',
          primary_color: data.primary_color || '#3B82F6',
          secondary_color: data.secondary_color || '#10B981',
          favicon_url: data.favicon_url || '',
        });
      },
    }
  );

  // Stripe connect status
  const { data: connectStatus, isLoading: stripeLoading } = useQuery(
    'onboarding-stripe-status',
    () => adminAPI.getStripeConnectStatus(),
    { select: (response) => response.data }
  );
  const { data: platformFeePolicy } = useQuery(
    'onboarding-platform-fee-policy',
    () => adminAPI.getPlatformFeePolicy(),
    { select: (response) => response.data },
  );
  const effectivePlatformFee = platformFeePolicy?.effective;
  const effectivePlatformFeePercent = effectivePlatformFee?.rate_percent;
  const effectivePlatformFeeSource = effectivePlatformFee?.source === 'tenant_custom'
    ? 'custom tenant rate'
    : 'inherited platform default';
  const effectivePlatformFeeDate = effectivePlatformFee?.effective_from
    ? new Date(effectivePlatformFee.effective_from).toLocaleDateString()
    : null;

  // Onboarding status for verify step
  const { data: onboardingStatus, refetch: refetchOnboarding } = useQuery(
    'onboarding-status',
    () => adminAPI.getOnboardingStatus(),
    { select: (response) => response.data }
  );

  // File upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File must be under 2MB');
      return;
    }
    setUploading(true);
    try {
      const response = await adminAPI.uploadLogo(file);
      setBranding({ ...branding, logo_url: response.data.url });
      toast.success('Logo uploaded');
    } catch {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  // Handle Stripe OAuth callback — backend already exchanged the code,
  // we just need to refresh status and clean up the URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isCallback = params.get('stripe_callback');
    const isError = params.get('stripe_error');

    if (isCallback) {
      toast.success('Stripe account connected successfully!');
      queryClient.invalidateQueries('onboarding-stripe-status');
      refetchOnboarding();
      setCurrentStep(3); // Jump to verify step
      window.history.replaceState({}, '', window.location.pathname);
    } else if (isError) {
      toast.error('Failed to connect Stripe account');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [queryClient, refetchOnboarding]);

  // Mutations
  const saveSettingsMutation = useMutation(
    () => adminAPI.updateGeneralSettings(companyInfo),
    {
      onSuccess: () => {
        toast.success('Company info saved');
        refetchOnboarding();
      },
      onError: () => { toast.error('Failed to save company info'); },
    }
  );

  const saveBrandingMutation = useMutation(
    () => adminAPI.updateBranding(branding),
    {
      onSuccess: () => {
        toast.success('Branding saved');
        refetchOnboarding();
      },
      onError: () => { toast.error('Failed to save branding'); },
    }
  );

  const testConnectionMutation = useMutation(
    () => adminAPI.testStripeConnection(),
    {
      onSuccess: (response) => {
        const result = response.data;
        if (result.success) {
          toast.success(result.message);
        } else {
          toast.error(result.message);
        }
      },
      onError: () => { toast.error('Failed to test connection'); },
    }
  );

  const completeMutation = useMutation(
    () => adminAPI.completeOnboarding(),
    {
      onSuccess: () => {
        toast.success('Onboarding complete! Welcome to ModalOS.');
        navigate('/admin');
      },
      onError: () => { toast.error('Failed to complete onboarding'); },
    }
  );

  const dismissMutation = useMutation(
    () => adminAPI.dismissOnboarding(),
    {
      onSuccess: () => navigate('/admin'),
    }
  );

  const handleConnectStripe = async () => {
    try {
      const response = await adminAPI.getStripeConnectOAuthLink('/admin/onboarding');
      window.location.href = response.data.url;
    } catch {
      toast.error('Failed to generate Stripe Connect link');
    }
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      await saveSettingsMutation.mutateAsync();
    } else if (currentStep === 1) {
      await saveBrandingMutation.mutateAsync();
    }
    setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  if (settingsLoading || brandingLoading) {
    return (
      <div className="max-w-4xl mx-auto px-page-gutter py-8">
        <LoadingSpinner className="py-20" />
      </div>
    );
  }

  const steps = onboardingStatus?.steps || {};

  return (
    <div className="max-w-4xl mx-auto px-page-gutter py-8">
      {/* Header */}
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-on-surface">Welcome to ModalOS</h1>
        <p className="mt-2 text-on-surface-variant">Let's get your company set up in a few quick steps</p>
      </div>

      {/* Step Indicator */}
      <div className="mb-10">
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <button
                onClick={() => setCurrentStep(index)}
                className="flex flex-col items-center"
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                  index === currentStep
                    ? 'bg-primary-600 text-white'
                    : index < currentStep
                    ? 'bg-green-500 text-white'
                    : 'bg-surface-container-low text-on-surface-variant'
                }`}>
                  {index < currentStep ? (
                    <CheckCircleIcon className="h-6 w-6" />
                  ) : (
                    <step.icon className="h-5 w-5" />
                  )}
                </div>
                <span className={`mt-2 text-xs font-medium ${
                  index === currentStep ? 'text-primary-600' : 'text-on-surface-variant'
                }`}>
                  {step.label}
                </span>
              </button>
              {index < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${
                  index < currentStep ? 'bg-green-500' : 'bg-surface-container-low'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="card p-8">
        {/* Step 1: Company Info */}
        {currentStep === 0 && (
          <div>
            <h2 className="text-xl font-semibold text-on-surface mb-6">Company Information</h2>
            <p className="text-sm text-on-surface-variant mb-6">
              This information will appear on invoices and customer communications.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Company Name</label>
                <input
                  type="text"
                  className="input-field"
                  value={companyInfo.companyName}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, companyName: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Support Email *</label>
                <input
                  type="email"
                  className="input-field"
                  value={companyInfo.supportEmail}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, supportEmail: e.target.value })}
                  placeholder="support@yourcompany.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Tax ID</label>
                <input
                  type="text"
                  className="input-field"
                  value={companyInfo.taxId}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, taxId: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Invoice Prefix</label>
                <input
                  type="text"
                  className="input-field"
                  value={companyInfo.invoicePrefix}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, invoicePrefix: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Timezone</label>
                <select
                  className="input-field"
                  value={companyInfo.timezone}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, timezone: e.target.value })}
                >
                  <option value="America/New_York">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                  <option value="America/Anchorage">Alaska Time</option>
                  <option value="Pacific/Honolulu">Hawaii Time</option>
                  <option value="Europe/London">GMT / London</option>
                  <option value="Europe/Paris">CET / Paris</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Company Address *</label>
                <textarea
                  className="input-field"
                  rows={3}
                  value={companyInfo.companyAddress}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, companyAddress: e.target.value })}
                  placeholder="123 Main St&#10;City, State ZIP"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Branding */}
        {currentStep === 1 && (
          <div>
            <h2 className="text-xl font-semibold text-on-surface mb-6">Branding</h2>
            <p className="text-sm text-on-surface-variant mb-6">
              Customize how your platform looks to customers.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">App Name</label>
                <input
                  type="text"
                  className="input-field"
                  value={branding.app_name}
                  onChange={(e) => setBranding({ ...branding, app_name: e.target.value })}
                  placeholder="Your Company Name"
                />
              </div>

              {/* Logo Upload */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Logo</label>
                <div
                  className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    dragOver ? 'border-blue-400 bg-blue-50' : 'border-outline-variant hover:border-outline'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file);
                    }}
                  />
                  {uploading ? (
                    <LoadingSpinner size="sm" />
                  ) : branding.logo_url ? (
                    <div className="flex items-center justify-center space-x-4">
                      <img
                        src={branding.logo_url}
                        alt="Logo preview"
                        className="h-16 w-16 rounded object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                      <div className="text-left">
                        <p className="text-sm text-on-surface-variant font-medium">Logo uploaded</p>
                        <p className="text-xs text-on-surface-variant">Click or drag to replace</p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <CloudArrowUpIcon className="h-10 w-10 text-on-surface-variant mx-auto mb-2" />
                      <p className="text-sm text-on-surface-variant">Click to upload or drag and drop</p>
                      <p className="text-xs text-on-surface-variant mt-1">PNG, JPG, GIF, WEBP up to 2MB</p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Primary Color</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    className="h-12 w-20 rounded cursor-pointer"
                    value={branding.primary_color}
                    onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                  />
                  <input
                    type="text"
                    className="input-field flex-1"
                    value={branding.primary_color}
                    onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Secondary Color</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    className="h-12 w-20 rounded cursor-pointer"
                    value={branding.secondary_color}
                    onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                  />
                  <input
                    type="text"
                    className="input-field flex-1"
                    value={branding.secondary_color}
                    onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })}
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-on-surface-variant mb-1">Favicon URL</label>
                <input
                  type="url"
                  className="input-field"
                  value={branding.favicon_url}
                  onChange={(e) => setBranding({ ...branding, favicon_url: e.target.value })}
                  placeholder="https://example.com/favicon.ico"
                />
              </div>

              {/* Live Preview */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-on-surface-variant mb-2">Preview</label>
                <div className="rounded-card overflow-hidden">
                  <div
                    className="px-4 py-3 flex items-center space-x-3"
                    style={{ backgroundColor: branding.primary_color }}
                  >
                    {branding.logo_url && (
                      <img
                        src={branding.logo_url}
                        alt="Logo"
                        className="h-8 w-8 rounded object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                    <span className="text-white font-semibold">
                      {branding.app_name || 'Your App Name'}
                    </span>
                  </div>
                  <div className="p-4 bg-surface-container-lowest">
                    <div className="flex space-x-3">
                      <button
                        className="px-4 py-2 rounded text-white text-sm font-medium"
                        style={{ backgroundColor: branding.primary_color }}
                      >
                        Primary Button
                      </button>
                      <button
                        className="px-4 py-2 rounded text-white text-sm font-medium"
                        style={{ backgroundColor: branding.secondary_color }}
                      >
                        Secondary Button
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Connect Stripe */}
        {currentStep === 2 && (
          <div>
            <h2 className="text-xl font-semibold text-on-surface mb-6">Connect Stripe</h2>
            <p className="text-sm text-on-surface-variant mb-6">
              Connect your Stripe account to start accepting payments. {typeof effectivePlatformFeePercent === 'number'
                ? `Your current ModalOS platform fee is ${effectivePlatformFeePercent}% on the applicable transaction basis. `
                : 'Your applicable ModalOS platform fee will appear after the policy loads. '}
              Stripe processing fees remain separate.
            </p>
            {typeof effectivePlatformFeePercent === 'number' && (
              <p className="-mt-4 mb-6 text-xs text-on-surface-variant">
                {effectivePlatformFeeSource}{effectivePlatformFeeDate ? ` effective ${effectivePlatformFeeDate}` : ''}.
              </p>
            )}

            {stripeLoading ? (
              <LoadingSpinner className="py-8" />
            ) : connectStatus?.connected ? (
              <div>
                <div className="flex items-center space-x-3 mb-6 p-4 bg-success-container rounded-card">
                  <CheckCircleIcon className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="font-medium text-green-800">Stripe Connected</p>
                    <p className="text-sm text-green-700">
                      Account: {connectStatus.accountId}
                      {connectStatus.businessName && ` (${connectStatus.businessName})`}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 mb-6">
                  <div className={`inline-flex items-center px-3 py-1 rounded-pill text-sm ${
                    connectStatus.chargesEnabled ? 'bg-success-container text-success-on-container' : 'bg-error-container text-error-on-container'
                  }`}>
                    {connectStatus.chargesEnabled ? 'Charges Enabled' : 'Charges Disabled'}
                  </div>
                  <div className={`inline-flex items-center px-3 py-1 rounded-pill text-sm ${
                    connectStatus.payoutsEnabled ? 'bg-success-container text-success-on-container' : 'bg-error-container text-error-on-container'
                  }`}>
                    {connectStatus.payoutsEnabled ? 'Payouts Enabled' : 'Payouts Disabled'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <CreditCardIcon className="h-16 w-16 text-on-surface-variant mx-auto mb-4" />
                <p className="text-on-surface-variant mb-6">No Stripe account connected yet</p>
                <button
                  onClick={handleConnectStripe}
                  className="btn-primary inline-flex items-center space-x-2"
                >
                  <LinkIcon className="h-4 w-4" />
                  <span>Connect with Stripe</span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Verify & Go Live */}
        {currentStep === 3 && (
          <div>
            <h2 className="text-xl font-semibold text-on-surface mb-6">Verify & Go Live</h2>
            <p className="text-sm text-on-surface-variant mb-6">
              Review your setup before going live.
            </p>

            <div className="space-y-4 mb-8">
              <div
                className="flex items-center justify-between p-4 rounded-card cursor-pointer hover:bg-surface-container-low"
                onClick={() => !steps.company_info && setCurrentStep(0)}
              >
                <div className="flex items-center space-x-3">
                  {steps.company_info ? (
                    <CheckCircleIcon className="h-6 w-6 text-green-500" />
                  ) : (
                    <XCircleIcon className="h-6 w-6 text-red-400" />
                  )}
                  <div>
                    <p className="font-medium text-on-surface">Company Information</p>
                    <p className="text-sm text-on-surface-variant">
                      {steps.company_info ? 'Address and support email configured' : 'Missing address or support email'}
                    </p>
                  </div>
                </div>
                {!steps.company_info && (
                  <span className="text-sm text-primary-600 font-medium">Fix</span>
                )}
              </div>

              <div
                className="flex items-center justify-between p-4 rounded-card cursor-pointer hover:bg-surface-container-low"
                onClick={() => !steps.branding && setCurrentStep(1)}
              >
                <div className="flex items-center space-x-3">
                  {steps.branding ? (
                    <CheckCircleIcon className="h-6 w-6 text-green-500" />
                  ) : (
                    <XCircleIcon className="h-6 w-6 text-red-400" />
                  )}
                  <div>
                    <p className="font-medium text-on-surface">Branding</p>
                    <p className="text-sm text-on-surface-variant">
                      {steps.branding ? 'Brand customization applied' : 'No branding configured'}
                    </p>
                  </div>
                </div>
                {!steps.branding && (
                  <span className="text-sm text-primary-600 font-medium">Fix</span>
                )}
              </div>

              <div
                className="flex items-center justify-between p-4 rounded-card cursor-pointer hover:bg-surface-container-low"
                onClick={() => !steps.stripe && setCurrentStep(2)}
              >
                <div className="flex items-center space-x-3">
                  {steps.stripe ? (
                    <CheckCircleIcon className="h-6 w-6 text-green-500" />
                  ) : (
                    <XCircleIcon className="h-6 w-6 text-red-400" />
                  )}
                  <div>
                    <p className="font-medium text-on-surface">Stripe Connection</p>
                    <p className="text-sm text-on-surface-variant">
                      {steps.stripe ? 'Stripe connected with charges enabled' : 'Stripe not connected or charges disabled'}
                    </p>
                  </div>
                </div>
                {!steps.stripe && (
                  <span className="text-sm text-primary-600 font-medium">Fix</span>
                )}
              </div>
            </div>

            {/* Test Connection */}
            {steps.stripe && (
              <div className="mb-6">
                <button
                  onClick={() => testConnectionMutation.mutate()}
                  disabled={testConnectionMutation.isLoading}
                  className="btn-secondary inline-flex items-center space-x-2"
                >
                  {testConnectionMutation.isLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <ArrowPathIcon className="h-4 w-4" />
                  )}
                  <span>Test Stripe Connection</span>
                </button>
              </div>
            )}

            {/* Complete Setup */}
            <button
              onClick={() => completeMutation.mutate()}
              disabled={completeMutation.isLoading}
              className="btn-primary inline-flex items-center space-x-2"
            >
              {completeMutation.isLoading && <LoadingSpinner size="sm" />}
              <span>Complete Setup</span>
            </button>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="mt-6 flex items-center justify-between">
        <div>
          {currentStep > 0 && (
            <button onClick={handleBack} className="btn-secondary">
              Back
            </button>
          )}
        </div>

        <button
          onClick={() => dismissMutation.mutate()}
          disabled={dismissMutation.isLoading}
          className="text-sm text-on-surface-variant hover:text-on-surface"
        >
          Skip to Dashboard
        </button>

        <div>
          {currentStep < STEPS.length - 1 && (
            <button
              onClick={handleNext}
              disabled={saveSettingsMutation.isLoading || saveBrandingMutation.isLoading}
              className="btn-primary inline-flex items-center space-x-2"
            >
              {(saveSettingsMutation.isLoading || saveBrandingMutation.isLoading) && (
                <LoadingSpinner size="sm" />
              )}
              <span>Next</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminOnboarding;
